import styled from 'styled-components'

/**
 * SureCo Design System "Dashboard Box", the same tokens Card Mode's tiles use
 * (VcnManagementMetricTiles.styles.ts). Kept in step deliberately: the two
 * modes sit behind one tab strip, and tiles that differ in padding, radius and
 * figure colour read as two different products.
 */
const TILE = {
  text: '#112a43',
  muted: '#617d99',
  border: '#bcccdc',
  hoverBackground: '#f0f4f8',
  hoverBorder: '#d9e2ec',
  activeBorder: '#0a939b',
} as const

export const Section = styled.div`
  width: 100%;
  min-width: 0;
`

export const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 12px;
`

export const HeaderTitle = styled.span`
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #8c8c8c;
`

export const AsOf = styled.span`
  font-size: 13px;
  color: #8c8c8c;
  white-space: nowrap;
`

/** Same flex-wrap behaviour as Card Mode's tiles: each sizes to its content and
    wraps, rather than being forced into equal columns. */
export const CardsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 16px;
`

export const Card = styled.button<{
  $accent: string
  $active: boolean
  $clickable: boolean
}>`
  position: relative;
  flex: 0 1 auto;
  min-width: 236px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: flex-start;
  padding: 12px;
  min-height: 92px;
  text-align: left;
  background: #fff;
  border: 1px solid
    ${({ $active }) => ($active ? TILE.activeBorder : TILE.border)};
  border-radius: 4px;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  transition:
    background 0.15s,
    border-color 0.15s;
  font: inherit;

  &:hover {
    background: ${({ $active, $clickable }) =>
      $active || !$clickable ? '#fff' : TILE.hoverBackground};
    border-color: ${({ $active }) =>
      $active ? TILE.activeBorder : TILE.hoverBorder};
  }
  &:disabled {
    cursor: default;
  }
`

/** The Dashboard Box pill, identical to Card Mode's. */
export const Chip = styled.span<{ $accent: string }>`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 8px;
  font-size: 10px;
  font-weight: 700;
  line-height: 16px;
  white-space: nowrap;
  border-radius: 4px;
  color: ${({ $accent }) => $accent};
  background: ${({ $accent }) => `${$accent}14`};
  border: 1px solid ${({ $accent }) => `${$accent}40`};
`

/** Neutral, not accent-coloured: the design gives the figure one colour across
    every tile and lets the pill carry the provider. */
export const Value = styled.span`
  font-size: 24px;
  font-weight: 600;
  line-height: 32px;
  color: ${TILE.text};
`

/** The metric's name. Carries the info affordance, so it is a row. */
export const Label = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: ${TILE.text};
`

/**
 * States how the number is counted, in the tile rather than behind a hover.
 * The 35-vs-7 confusion at the 2026-09-15 demo was a reader having no way to
 * see which benefits a figure included, and the explanation sat behind a
 * three-second tooltip nobody waits for.
 */
export const Caption = styled.span`
  width: 100%;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  color: ${TILE.muted};
`

export const Banner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 16px;
  margin-bottom: 12px;
  background: #e6fffb;
  border: 1px solid #87e8de;
  border-radius: 10px;
`

export const BannerText = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #006d75;

  strong {
    color: #00474f;
  }
`
